import { getTimezoneOffset, getTimezone } from '../utils/apiUtils'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'

export interface ReportFilters {
  userId?: string
  startDate: string
  endDate: string
  projectIds?: string[]
  status?: string[]
}

export interface SummaryData {
  totalHours: number
  projectCount: number
  totalAmount: number
  averageQualityScore?: number
  totalTeamCost?: number
  budgetUtilization?: number
}

export interface DailyHoursData {
  date: string
  hours: number
}

export interface ProjectDistributionData {
  projectId: string
  projectName: string
  projectColor: string
  hours: number
  percentage: number
}

export interface TeamCostDistributionData {
  userId: string
  userName: string
  hours: number
  cost: number
  hourlyRate: number
  percentage: number
}

export interface ReportUser {
  id: string
  name: string
  email: string
  role: string
}

export interface ReportProject {
  id: string
  name: string
  color: string
}

export interface TimeEntryDetail {
  id: string
  date: string
  projectId: string
  projectName: string
  projectColor: string
  description: string
  startTime: string
  endTime: string
  duration: number // 秒數
  status: string
  userName?: string // 管理員可見
}

export interface TimeEntriesDetailResponse {
  entries: TimeEntryDetail[]
  hasMore: boolean
  totalCount: number
  lastDate?: string
}

class ReportService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('token')
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.data
  }

  // 將日期字符串轉換為完整的時間範圍（用於 API 請求）
  private formatDateForAPI(dateStr: string, isEndDate: boolean = false): string {
    // 解析日期字符串 "YYYY-MM-DD"
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day) // month 是從 0 開始的
    
    if (isEndDate) {
      // 設定為當天結束時間 23:59:59.999
      date.setHours(23, 59, 59, 999)
    } else {
      // 設定為當天開始時間 00:00:00.000
      date.setHours(0, 0, 0, 0)
    }
    
    return date.toISOString()
  }

  // 轉換篩選器中的日期格式
  private convertFiltersForAPI(filters: ReportFilters): ReportFilters {
    return {
      ...filters,
      startDate: this.formatDateForAPI(filters.startDate, false),
      endDate: this.formatDateForAPI(filters.endDate, true)
    }
  }

  private buildQueryString(params: Record<string, any>, includeTimezone: boolean = false): string {
    const searchParams = new URLSearchParams()
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(key, item))
        } else {
          searchParams.append(key, value.toString())
        }
      }
    })
    
    // 添加時區信息（針對需要時區感知的 API）
    if (includeTimezone) {
      searchParams.append('timezoneOffset', getTimezoneOffset().toString())
      searchParams.append('timezone', getTimezone())
    }
    
    return searchParams.toString()
  }

  async getSummaryData(filters: ReportFilters): Promise<SummaryData> {
    const apiFilters = this.convertFiltersForAPI(filters)
    const queryString = this.buildQueryString(apiFilters, true) // 包含時區信息
    return this.request<SummaryData>(`/reports/summary?${queryString}`)
  }

  async getDailyHoursData(filters: ReportFilters): Promise<DailyHoursData[]> {
    const apiFilters = this.convertFiltersForAPI(filters)
    const queryString = this.buildQueryString(apiFilters, true) // 包含時區信息
    return this.request<DailyHoursData[]>(`/reports/daily-hours?${queryString}`)
  }

  async getProjectDistribution(filters: ReportFilters): Promise<ProjectDistributionData[]> {
    const apiFilters = this.convertFiltersForAPI(filters)
    const queryString = this.buildQueryString(apiFilters, true) // 包含時區信息
    return this.request<ProjectDistributionData[]>(`/reports/project-distribution?${queryString}`)
  }

  async getTeamCostDistribution(filters: Omit<ReportFilters, 'userId'>): Promise<TeamCostDistributionData[]> {
    const apiFilters = this.convertFiltersForAPI(filters as ReportFilters)
    const queryString = this.buildQueryString(apiFilters, true) // 包含時區信息
    return this.request<TeamCostDistributionData[]>(`/reports/team-cost-distribution?${queryString}`)
  }

  async getUsers(): Promise<ReportUser[]> {
    return this.request<ReportUser[]>('/reports/users')
  }

  async getProjects(): Promise<ReportProject[]> {
    return this.request<ReportProject[]>('/reports/projects')
  }

  async getTimeEntriesDetail(filters: ReportFilters, offset: number = 0, limit: number = 100): Promise<TimeEntriesDetailResponse> {
    const apiFilters = this.convertFiltersForAPI(filters)
    const params = {
      ...apiFilters,
      offset,
      limit
    }
    const queryString = this.buildQueryString(params, true) // 包含時區信息
    return this.request<TimeEntriesDetailResponse>(`/reports/time-entries-detail?${queryString}`)
  }

  // 輔助方法：獲取預設日期範圍
  getDefaultDateRange(type: 'today' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth'): { startDate: string; endDate: string } {
    const now = new Date()
    
    // 使用本地時間格式化日期，避免時區轉換問題
    const formatDate = (date: Date): string => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    
    switch (type) {
      case 'today':
        return {
          startDate: formatDate(now),
          endDate: formatDate(now)
        }
      
      case 'thisWeek': {
        const today = new Date(now)
        const dayOfWeek = today.getDay()
        // 計算本週一（如果今天是星期日，則往前推 6 天到上週一）
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
        
        const startOfWeek = new Date(today)
        startOfWeek.setDate(today.getDate() - daysToMonday)
        
        const endOfWeek = new Date(startOfWeek)
        endOfWeek.setDate(startOfWeek.getDate() + 6)
        
        return {
          startDate: formatDate(startOfWeek),
          endDate: formatDate(endOfWeek)
        }
      }
      
      case 'lastWeek': {
        const today = new Date(now)
        const dayOfWeek = today.getDay()
        // 計算本週一（如果今天是星期日，則往前推 6 天到上週一）
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
        
        const startOfThisWeek = new Date(today)
        startOfThisWeek.setDate(today.getDate() - daysToMonday)
        
        // 上週一是本週一往前推 7 天
        const startOfLastWeek = new Date(startOfThisWeek)
        startOfLastWeek.setDate(startOfThisWeek.getDate() - 7)
        
        // 上週日是上週一往後推 6 天
        const endOfLastWeek = new Date(startOfLastWeek)
        endOfLastWeek.setDate(startOfLastWeek.getDate() + 6)
        
        return {
          startDate: formatDate(startOfLastWeek),
          endDate: formatDate(endOfLastWeek)
        }
      }
      
      case 'thisMonth': {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        
        return {
          startDate: formatDate(startOfMonth),
          endDate: formatDate(endOfMonth)
        }
      }
      
      case 'lastMonth': {
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0)
        
        return {
          startDate: formatDate(startOfLastMonth),
          endDate: formatDate(endOfLastMonth)
        }
      }
      
      default:
        return {
          startDate: formatDate(now),
          endDate: formatDate(now)
        }
    }
  }

  // 輔助方法：格式化金額
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  // 輔助方法：格式化工時
  formatHours(hours: number): string {
    const wholeHours = Math.floor(hours)
    const minutes = Math.round((hours - wholeHours) * 60)
    
    if (minutes === 0) {
      return `${wholeHours} 小時`
    }
    
    return `${wholeHours} 小時 ${minutes} 分鐘`
  }

  // 輔助方法：獲取品質分數顏色
  getQualityScoreColor(score?: number): string {
    if (score === undefined) return 'text-gray-500'
    if (score >= 90) return 'text-green-600'
    if (score >= 80) return 'text-blue-600'
    if (score >= 70) return 'text-yellow-600'
    if (score >= 60) return 'text-orange-600'
    return 'text-red-600'
  }

  // 輔助方法：獲取品質分數標籤
  getQualityScoreLabel(score?: number): string {
    if (score === undefined) return '未評分'
    if (score >= 90) return '優秀'
    if (score >= 80) return '良好'
    if (score >= 70) return '一般'
    if (score >= 60) return '待改善'
    return '需關注'
  }
}

export const reportService = new ReportService()
